import React, { useMemo } from 'react';
import { CountBadge, FlexyTabs, TabStructure } from '@europrocurement/flexy-components';
import { Box } from '@mui/material';
import {
    ActivityTab,
    BillingTab,
    DocumentPreviewOverlay,
    DocumentSignTab,
    DossierFormulaireProvider,
    DossierSignDetailOverlay,
    DownloadableDocumentsTab,
    RequiredDocumentsStatusAction,
    RequiredDocumentsTab,
    useCurrentDossierCtx,
    useDossierDocumentsCtx,
    DOSSIER_STATUS_DRAFT,
    DOSSIER_STATUS_IN_PROGRESS,
    DOSSIER_STATUS_AWAITING,
    DOSSIER_STATUS_INCOMPLETE,
    DOSSIER_STATUS_TO_CHECK,
    CounterTabNotification,
} from '@europrocurement/l2d-modules';
import { DOC_TYPE_DOWNLOADABLE, DOC_TYPE_REQUIRED } from '@europrocurement/l2d-domain';
import InformationsTab from '../InformationsTab/InformationsTab';
import { MessageTab } from '../MessageTab/MessageTab';
import FormulaireTab from '../FormulaireTab/FormulaireTab';

const PREPARATION_STATUSES = [
    DOSSIER_STATUS_DRAFT,
    DOSSIER_STATUS_IN_PROGRESS,
    DOSSIER_STATUS_AWAITING,
    DOSSIER_STATUS_INCOMPLETE,
    DOSSIER_STATUS_TO_CHECK,
];

const DossierBody: React.FunctionComponent = function () {
    const { getMissingDocumentsCountByCategory, getReceivedDocumentsCountByCategory } =
        useDossierDocumentsCtx();

    const { dossier } = useCurrentDossierCtx();

    const missingDocumentsCount = getMissingDocumentsCountByCategory({
        categoryId: DOC_TYPE_REQUIRED,
        dossierId: dossier?.id as number,
    });

    const downloadableDocuments = getReceivedDocumentsCountByCategory({
        categoryId: DOC_TYPE_DOWNLOADABLE,
    });

    const dossierFormulaireIds = useMemo(
        () => (dossier?.id ? [dossier.id as number] : []),
        [dossier?.id],
    );

    const showFormulaireTab = useMemo(
        () => PREPARATION_STATUSES.includes(dossier?.statutId as number),
        [dossier?.statutId],
    );

    const tabs: Array<TabStructure> = [
        {
            tabName: 'details',
            tabTitle: 'Détails du dossier',
            tabPanel: <InformationsTab />,
        },
        ...(showFormulaireTab
            ? [
                  {
                      tabName: 'formulaire',
                      tabTitle: 'Formulaire',
                      tabPanel: <FormulaireTab />,
                  },
              ]
            : []),
        {
            tabName: 'required-document',
            tabTitle: (
                <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    Documents requis{' '}
                    {missingDocumentsCount > 0 && (
                        <CountBadge
                            count={missingDocumentsCount}
                            color="warning"
                            bgVariant="main"
                            contrastText
                        />
                    )}
                </Box>
            ),
            tabPanel: (
                <RequiredDocumentsTab source="FRONT">
                    {/* Row Button Action to change status by prescriber on invalid dossier status */}
                    <RequiredDocumentsStatusAction />
                </RequiredDocumentsTab>
            ),
        },
        {
            tabName: 'downloadable-document',
            tabTitle: (
                <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    Documents remis{' '}
                    {downloadableDocuments > 0 && (
                        <CountBadge
                            count={downloadableDocuments}
                            color="primary"
                            bgVariant="light"
                        />
                    )}
                </Box>
            ),
            tabPanel: <DownloadableDocumentsTab source="FRONT" />,
        },
        {
            tabName: 'billing',
            tabTitle: 'Facturation',
            tabPanel: <BillingTab />,
        },
        {
            tabName: 'message',
            tabTitle: (
                <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    Messages <CounterTabNotification dossier={dossier} />
                </Box>
            ),
            tabPanel: <MessageTab />,
        },
        {
            tabName: 'signatures-electronique',
            tabTitle: 'Signature électronique',
            tabPanel: (
                <>
                    <DocumentSignTab />
                    <DossierSignDetailOverlay />
                    <DocumentPreviewOverlay />
                </>
            ),
        },
        {
            tabName: 'historique',
            tabTitle: 'Historique',
            tabPanel: (
                <ActivityTab
                    showTask={false}
                    showTechniqueLogs={false}
                    noteMemo={false}
                    showNotes={false}
                    showLogData={false}
                />
            ),
        },
    ];

    return (
        <Box sx={{ pointerEvents: dossier?.deleted ? 'none' : 'auto' }}>
            <DossierFormulaireProvider idDossiers={dossierFormulaireIds}>
                <FlexyTabs
                    routerParameterName="dossier-detail"
                    sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}
                    tabs={tabs}
                    scrollbar={false}
                    indicatorColor="primary"
                />
            </DossierFormulaireProvider>
        </Box>
    );
};

export default DossierBody;
